import { CitationLocation, ResolvedCitation } from './types'

// Databox/uploaded documents are keyed on the backend by a filename such as
// "a2750ec3e17748939b2212c4b6c3a068_1787828787614_M&G CNH User Guide 2026.pdf": a storage-side
// prefix, an upload timestamp, then the original display name, underscore-joined. That leading
// prefix is NOT the document's real id (calling document/blob/{prefix} 404s) — it's only useful
// here to recover the clean, human-readable display name.
const ID_TIMESTAMP_NAME_PATTERN = /^([0-9a-f]{6,40})_(\d{6,})_(.+)$/i
// Older/simpler convention: a numeric id followed by the display name (see getFileDisplayName).
const ID_NAME_PATTERN = /^(\d+)_(.+)$/

const parseDocReference = (reference?: string): { displayName?: string } => {
  if (!reference) return {}

  let match = reference.match(ID_TIMESTAMP_NAME_PATTERN)
  if (match) return { displayName: match[3] }

  match = reference.match(ID_NAME_PATTERN)
  if (match) return { displayName: match[2] }

  return { displayName: reference }
}

// The chat API never puts the real document id on the citation itself — it only appears on the
// resources[] array of the user message that uploaded the file (e.g.
// { id: 'ee4de268-c9bd-4ce8-9777-9c351bf7bc82', name: 'M&G CNH User Guide 2026.pdf' }). Recover it
// by matching that resource's original filename against the name embedded in the citation's
// storage key.
const getResourceName = (resource: any): string | undefined =>
  resource?.name || resource?.fileName || resource?.originalFileName || resource?.url

const getResourceId = (resource: any): string | undefined =>
  resource?.id ||
  resource?.documentId ||
  resource?.fileId ||
  resource?.smartDocsDocumentId ||
  resource?.externalId

export const normalizeCitationFileName = (fileName?: string): string => {
  if (!fileName) return ''
  let decoded = fileName
  try {
    decoded = decodeURIComponent(fileName)
  } catch {
    // Keep malformed legacy names matchable instead of failing the entire preview flow.
  }
  const baseName = decoded.split(/[\\/]/).pop() || decoded
  return baseName
    .replace(/^([0-9a-f]{6,40})_\d{6,}_/i, '')
    .replace(/^\d+_/, '')
    .replace(/\.[a-z0-9]{2,5}$/i, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
}

const findResourceByName = (displayName: string | undefined, resources: any[]): any | undefined => {
  if (!displayName || !resources?.length) return undefined
  const normalized = normalizeCitationFileName(displayName)
  return resources.find((resource) => {
    const { displayName: resourceDisplayName } = parseDocReference(getResourceName(resource))
    return normalizeCitationFileName(resourceDisplayName) === normalized
  })
}

const getTextSnippet = (raw: any): string | undefined => {
  const candidates = [
    raw?.sourceText,
    raw?.source_text,
    raw?.snippet,
    raw?.text,
    raw?.preview,
    raw?.excerpt,
    raw?.quote,
    raw?.content,
    raw?.pageContent,
    raw?.page_content,
    raw?.chunkText,
    raw?.chunk_text,
    raw?.metadata?.sourceText,
    raw?.metadata?.snippet,
    raw?.metadata?.preview,
    raw?.metadata?.content
  ]
  const snippet = candidates.find((candidate) => typeof candidate === 'string' && candidate.trim())
  return snippet?.trim()
}

const toLocation = (raw: any): CitationLocation => {
  // Backend has been observed sending `page` as either a number or a numeric string, so coerce
  // rather than requiring a strict `number` type — a silently-dropped page number is what causes
  // the preview to fall back to page 1 instead of jumping to the cited page.
  const rawPage = raw?.page ?? raw?.pageNumber
  const parsedPage = Number(rawPage)
  return {
    pageNumber:
      rawPage !== undefined && rawPage !== null && !Number.isNaN(parsedPage)
        ? parsedPage
        : undefined,
    startOffset: raw?.startOffset,
    endOffset: raw?.endOffset,
    textSnippet: getTextSnippet(raw)
  }
}

/**
 * Normalizes the loosely-typed "doc" citation source rendered by the Citations panel into the
 * strict Citation contract used by the preview feature. Accepts either a single citation
 * occurrence or the already-grouped `{ ...source, page: number[] }` shape produced when several
 * occurrences reference the same document.
 *
 * `messageResources` is the uploading user message's `resources` array (`{ id, name }[]`) — the
 * only place the document's real backend id lives; the citation object itself only ever carries
 * a storage filename. When no id can be resolved, `fileId` comes back as an empty string rather
 * than returning null, so the caller still opens the preview panel and the hook's existing
 * 'missing-document' error state explains it, instead of the click silently doing nothing.
 */
export const normalizeDocCitation = (
  rawSource: any,
  messageResources: any[] = []
): ResolvedCitation | null => {
  if (!rawSource) return null

  const occurrences: any[] = Array.isArray(rawSource.locations)
    ? rawSource.locations
    : Array.isArray(rawSource.page)
    ? rawSource.page.map((page: number) => ({ ...rawSource, page }))
    : [rawSource]

  const rawReference: string | undefined = rawSource.reference || rawSource.url || rawSource.name
  const { displayName } = parseDocReference(rawReference)
  const matchingResource = findResourceByName(displayName, messageResources)

  const fileId: string =
    rawSource.docId ||
    rawSource.documentId ||
    rawSource.fileId ||
    rawSource.smartDocsDocumentId ||
    rawSource.externalId ||
    getResourceId(matchingResource) ||
    ''
  const renditionId: string | undefined =
    rawSource.smartDocsDocumentId || matchingResource?.smartDocsDocumentId

  const locations: CitationLocation[] = occurrences
    .map(toLocation)
    .filter((location) => location.pageNumber !== undefined || location.textSnippet)

  const title =
    displayName || rawSource.name || rawSource.title || rawReference || 'Untitled document'

  // The panel always opens on `locations[0]`, so a location missing a page number (e.g. one with
  // only a text snippet) must not be allowed to sit ahead of one that has a real page to jump to.
  const firstLocationWithPage = locations.find((location) => location.pageNumber !== undefined)

  return {
    citationId: rawSource.citationId || fileId,
    fileId,
    renditionId,
    title,
    databoxId: rawSource.databoxId || rawSource.smartDocsDataboxId || rawSource.teamId || '',
    pageNumber: firstLocationWithPage?.pageNumber,
    locations
  }
}
